import React, { useState, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { Upload, UserRound, X, AlertTriangle } from 'lucide-react';
import { Logo } from './Logo';
import { useApp, CurrentUser } from './store';
import { downscaleImage } from './image';
// Neutral fallback used only if the person doesn't upload a photo.
const DEFAULT_AVATAR = 'https://i.pravatar.cc/160?img=68';
// Blocks the app on first visit until the person tells us who they are. Their
// choice is stored per browser (no password), so they are never asked again on
// this machine — posts, comments, kudos, and the top-bar avatar all reflect them.
export function IdentityGate() {
  const {
    persistUser,
    setIdentity,
    startBrowsing
  } = useApp();
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: '',
    title: '',
    location: '',
    avatar: ''
  });
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [saveWarning, setSaveWarning] = useState(false);
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Please choose an image under 8 MB.');
      return;
    }
    setError('');
    setProcessing(true);
    try {
      // Downscale + compress so the stored profile stays tiny.
      const small = await downscaleImage(file, 256, 0.8);
      setForm(f => ({
        ...f,
        avatar: small
      }));
    } catch {
      setError('Sorry, we couldn’t process that image. Try a different one.');
    } finally {
      setProcessing(false);
    }
  };
  const buildIdentity = (): CurrentUser => {
    const slug = form.name.trim().toLowerCase().replace(/\s+/g, '.');
    return {
      name: form.name.trim(),
      title: form.title.trim() || 'Team Member',
      location: form.location.trim() || 'Independence Home Loans',
      email: `${slug}@indephl.com`,
      avatar: form.avatar || DEFAULT_AVATAR
    };
  };
  const save = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!form.name.trim()) return;
    const identity = buildIdentity();
    // Self-check: try to persist first. If it fails, warn instead of silently
    // continuing (the person would otherwise be asked again next time).
    const ok = persistUser(identity);
    if (ok) {
      setIdentity(identity);
    } else {
      setSaveWarning(true);
    }
  };
  const continueAnyway = () => {
    // Commit in memory for this session even though it couldn't be saved.
    setIdentity(buildIdentity());
  };
  return <div className="fixed inset-0 z-[100] bg-navy-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-133">
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.25
    }} className="w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl" data-id="element-134">
        {/* Header */}
        <div className="bg-navy-600 px-6 py-5 text-center" data-id="element-135">
          <div className="flex justify-center mb-3" data-id="element-136">
            <Logo className="h-10" data-id="element-137" />
          </div>
          <h1 className="text-xl font-serif font-bold text-white" data-id="element-138">
            Welcome — who's here?
          </h1>
          <p className="text-navy-100 text-sm mt-1" data-id="element-139">
            Tell us who you are so your posts and kudos show up as you. We'll
            remember you on this computer.
          </p>
        </div>

        <form onSubmit={save} className="p-5 space-y-4" data-id="element-140">
          {/* Photo upload */}
          <div className="flex items-center gap-4" data-id="element-141">
            <div className="relative shrink-0" data-id="element-142">
              {form.avatar ? <img src={form.avatar} alt="Your photo" className="w-16 h-16 rounded-full object-cover ring-2 ring-gold-400" data-id="element-143" /> : <div className="w-16 h-16 rounded-full bg-navy-50 text-navy-300 flex items-center justify-center ring-1 ring-navy-100" data-id="element-144">
                  <UserRound className="w-7 h-7" data-id="element-145" />
                </div>}
              {form.avatar && <button type="button" onClick={() => setForm(f => ({
              ...f,
              avatar: ''
            }))} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-navy-600 text-white flex items-center justify-center hover:bg-navy-700" aria-label="Remove photo" data-id="element-146">
                  <X className="w-3 h-3" data-id="element-147" />
                </button>}
            </div>
            <div data-id="element-148">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={processing} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-navy-700 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors disabled:opacity-50" data-id="element-149">
                <Upload className="w-4 h-4" data-id="element-150" />
                {processing ? 'Processing…' : form.avatar ? 'Change photo' : 'Upload photo'}
              </button>
              <p className="text-xs text-navy-400 mt-1.5" data-id="element-151">
                Optional · we’ll resize it automatically.
              </p>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-id="element-152" />
            </div>
          </div>
          {error && <p className="text-sm text-red-600" data-id="element-153">{error}</p>}

          <label className="block" data-id="element-154">
            <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-155">
              Your name
            </span>
            <input autoFocus value={form.name} onChange={e => setForm(f => ({
            ...f,
            name: e.target.value
          }))} placeholder="Jane Doe" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-156" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3" data-id="element-157">
            <label className="block" data-id="element-158">
              <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-159">
                Title
              </span>
              <input value={form.title} onChange={e => setForm(f => ({
              ...f,
              title: e.target.value
            }))} placeholder="Loan Officer" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-160" />
            </label>
            <label className="block" data-id="element-161">
              <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-162">
                Location
              </span>
              <input value={form.location} onChange={e => setForm(f => ({
              ...f,
              location: e.target.value
            }))} placeholder="Dallas, TX" className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-163" />
            </label>
          </div>

          {/* Persistence warning (only shown if the save self-check failed) */}
          {saveWarning && <div className="flex items-start gap-2.5 p-3 rounded-lg bg-gold-50 border border-gold-200" data-id="element-164">
              <AlertTriangle className="w-4 h-4 text-gold-700 mt-0.5 shrink-0" data-id="element-165" />
              <p className="text-sm text-navy-700" data-id="element-166">
                We couldn’t save your profile on this device (browser storage
                may be full or blocked). You can continue for now, but you may
                be asked again next time.
              </p>
            </div>}

          <div className="flex items-center justify-between pt-1" data-id="element-167">
            {/* Read-only escape hatch. Lets a first-time visitor look around
             without an identity — posting/commenting/kudos stay gated until
             they introduce themselves. */}
            <button type="button" onClick={startBrowsing} className="text-sm font-medium text-navy-500 hover:text-navy-800 underline underline-offset-2 transition-colors" data-id="element-168">
              Just browsing for now
            </button>
            {saveWarning ? <button type="button" onClick={continueAnyway} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-169">
                Continue anyway
              </button> : <button type="submit" disabled={!form.name.trim() || processing} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-170">
                Continue
              </button>}
          </div>
          <p className="text-center text-xs text-navy-400" data-id="element-171">
            You can add your details anytime — just try to post.
          </p>
        </form>
      </motion.div>
    </div>;
}